import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const PageViewChart = ({ responseData = [], micrositeName = "", term = "monthly", year, monthName, quarter }) => {
  // Normalize microsite name for case-insensitive matching
  const normalizedMicrosite = useMemo(() => {
    if (!micrositeName) return "";
    return micrositeName.toString().toLowerCase().replace(/[_%20]+/g, " ").trim();
  }, [micrositeName]);

  // Filter responseData for this microsite (safe even if normalizedMicrosite === "")
  const filteredData = useMemo(() => {
    if (!Array.isArray(responseData)) return [];
    return responseData.filter((item) => {
      const normalizedPath = (item.path || "").toString().toLowerCase().replace(/[_%20]+/g, " ").trim();
      // If normalizedMicrosite is empty, include everything; otherwise look for substring
      return normalizedMicrosite === "" ? true : normalizedPath.includes(normalizedMicrosite);
    });
  }, [responseData, normalizedMicrosite]);

  // Month names (with year suffix)
  const monthNames = useMemo(() => {
    const y = year ?? new Date().getFullYear();
    return [
      `January ${y}`,
      `February ${y}`,
      `March ${y}`,
      `April ${y}`,
      `May ${y}`,
      `June ${y}`,
      `July ${y}`,
      `August ${y}`,
      `September ${y}`,
      `October ${y}`,
      `November ${y}`,
      `December ${y}`,
    ];
  }, [year]);

  // Helper: convert a month input (either numeric "10" or name "October") to a 0-based month index (0..11).
  const toMonthIndex = (m) => {
    if (m === undefined || m === null || m === "") return null;
    // If it's already a number-like string or number
    const n = Number(m);
    if (!Number.isNaN(n) && Number.isFinite(n)) {
      // numeric month expected in 1..12
      if (n >= 1 && n <= 12) return n - 1;
      return null;
    }
    // Otherwise try to parse as month name
    const parsed = new Date(`${m} 1, ${year ?? new Date().getFullYear()}`);
    if (!Number.isNaN(parsed.getTime())) return parsed.getMonth();
    return null;
  };

  // Build labels based on term
  const labels = useMemo(() => {
    if (term === "yearly") {
      return monthNames;
    }

    if (term === "quarterly") {
      const q = Number(quarter) || 1;
      const quarters = {
        1: [0, 1, 2],
        2: [3, 4, 5],
        3: [6, 7, 8],
        4: [9, 10, 11],
      };
      const monthIdxs = quarters[q] || [];
      return monthIdxs.map((i) => monthNames[i]);
    }

    // monthly
    const mi = toMonthIndex(monthName);
    if (mi === null) {
      // fallback — show current month if not provided
      const todayMonth = new Date().getMonth();
      return [monthNames[todayMonth]];
    }
    return [monthNames[mi]];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, monthNames, quarter, monthName, year]);

  // Convert a label like "October 2025" to backend key "202510"
  const labelToKey = (label) => {
    // label expected like "October 2025" -> take the month name and year
    if (!label) return null;
    const parts = label.split(" ");
    if (parts.length < 2) return null;
    const monthLabel = parts[0];
    const yearLabel = parts[1];
    const monthIndex = new Date(`${monthLabel} 1, ${yearLabel}`).getMonth() + 1; // 1..12
    return `${yearLabel}${String(monthIndex).padStart(2, "0")}`;
  };

  // Build datapoints (always numeric values)
  const dataPoints = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];

    if (term === "yearly" || term === "quarterly") {
      return labels.map((label) => {
        const key = labelToKey(label);
        if (!key) return 0;
        const match = filteredData.find((d) => String(d.month) === String(key));
        return match ? Number(match.viewers ?? match.views ?? 0) : 0;
      });
    }

    // monthly
    // Sum all entries in filteredData whose month === monthKey
    const singleLabel = labels[0];
    const monthKey = labelToKey(singleLabel);
    if (!monthKey) return [0];
    const totalViewers = filteredData
      .filter((d) => String(d.month) === String(monthKey))
      .reduce((sum, d) => sum + (Number(d.viewers ?? d.views ?? 0) || 0), 0);
    return labels.map(() => totalViewers);
  }, [filteredData, labels, term]);

  // Y axis scaling (safe even when dataPoints empty)
  const safeDataPoints = dataPoints.length ? dataPoints : [0];
  const minValue = Math.min(...safeDataPoints, 0);
  const maxValue = Math.max(...safeDataPoints, 0);

  let yMin, yMax;
  if (maxValue === 0 && minValue === 0) {
    yMin = -3;
    yMax = 3;
  } else {
    const range = Math.max(1, maxValue - minValue);
    yMin = Math.max(0, Math.floor(minValue - range * 0.2));
    yMax = Math.ceil(maxValue + range * 0.2);
  }

  // Chart config
  const chartData = {
    labels,
    datasets: [
      {
        label: `Views`,
        data: dataPoints,
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
        tension: 0.2,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: yMin,
        max: yMax,
        beginAtZero: yMin === 0,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `Views: ${context.parsed.y}`,
          title: (context) => context?.[0]?.label ?? "",
        },
      },
    },
  };

  return (
    <div style={{ height: "300px", width: "100%", marginBottom: 20 }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default PageViewChart;


